import type { ComponentProps, PropsWithChildren, ReactNode } from 'react';
import React, { useId } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type {
  AlignItemsProps,
  BackgroundColorProps,
  DisplayProps,
  FlexProps,
  FontSizeProps,
  LineHeightProps,
  MinHeightProps,
  MinWidthProps,
  OpacityProps,
  PositionProps,
  TextAlignProps,
} from 'styled-system';
import {
  alignItems,
  backgroundColor,
  color,
  display,
  flex,
  minHeight,
  minWidth,
  opacity,
  position,
  space,
  textAlign,
  themeGet,
} from 'styled-system';

import type { FillProps, SpaceProps, StrokeProps } from 'common/theme/system-types';
import { Direction } from '../../enums/Direction';
import { CheckboxAnalyticsValue } from '../../enums/form';
import { space as spaceHelper } from '../../theme/helper';
import { outlineCSS } from '../../theme/styles';
import { fill, stroke } from '../../theme/system-utilities';
import { dispatchTrackingEventGA4 } from '../../tracking/dispatchTrackingEventGA4';
import type { TrackingParams } from '../../types/tracking';
import type { IconProps } from '../Icon';
import Icon from '../Icon';
import ImageOption from '../ImageOption';
import { linkStyles } from '../Link';
import type { CommonTypographyProps } from '../Typography';
import { BodyRegularBR2 } from '../Typography';
import VisuallyHidden from '../VisuallyHidden';

export interface CheckboxProps extends PropsWithChildren {
  label?: string | ReactNode;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  checked?: boolean;
  disabled?: boolean;
  value?: string;
  name?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  className?: string;
  /** Direction, can reverse label and input */
  direction?: Direction;
  imageUrl?: string;
  imageAlt?: string;
  iconSize?: number;
  eventName?: string;
  trackingParams?: TrackingParams;
  hasError?: boolean;
  imageSize?: number;
  fillImage?: boolean;
  tabIndex?: number;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

type LabelTextProps = FlexProps & TextAlignProps;
type CheckboxIconProps = SpaceProps &
  DisplayProps &
  MinHeightProps &
  MinWidthProps &
  StrokeProps &
  BackgroundColorProps &
  FillProps &
  Pick<IconProps, 'size'> &
  Pick<CheckboxProps, 'direction'>;

const CHECKBOX_SIZE = 24;

const getCheckboxIconName = ({ checked }: Pick<CheckboxProps, 'checked'>) =>
  checked ? 'checkboxActive' : 'checkboxDefault';

const errorStyles = props => {
  return (
    props.hasError &&
    css`
      > input:not(:disabled) + svg {
        stroke: ${props.theme.colors.sys.error.text.default};
      }
    `
  );
};

const Label = styled(BodyRegularBR2)<
  DisplayProps &
    FontSizeProps &
    LineHeightProps &
    AlignItemsProps &
    FlexProps & {
      htmlFor: string;
    } & Pick<CheckboxProps, 'disabled' | 'direction' | 'checked'> & { hasError?: boolean }
>`
  ${display};
  ${alignItems};
  ${flex};
  ${color};

  flex-direction: ${({ direction }) => (direction === Direction.Rtl ? 'row-reverse' : 'row')};

  cursor: ${({ disabled }) => !disabled && 'pointer'};
  color: ${({ disabled }) =>
    themeGet(disabled ? 'colors.sys.neutral.text.inactive' : 'colors.sys.neutral.text.default')};

  ${errorStyles};

  &:hover {
    > input:not(:disabled) + svg {
      fill: ${themeGet('colors.sys.primary.background.hover')};
    }
  }

  &:hover {
    > input:not(:disabled):not(:checked) + svg {
      stroke: ${themeGet('colors.sys.neutral.border.hover')};
      fill: ${themeGet('colors.sys.neutral.background.hover')};
    }
  }
`;

const labelStyles: CommonTypographyProps & DisplayProps & AlignItemsProps & FlexProps = {
  as: 'label',
  color: 'sys.neutral.border.hover',
  display: 'flex',
  alignItems: 'center',
  flex: '1 1 0%',
};

const Input = styled.input<PositionProps & OpacityProps>`
  ${position};
  ${opacity};

  cursor: inherit;

  &:checked + svg {
    stroke: none;
    fill: ${themeGet('colors.sys.primary.background.default')};
  }

  &:not(:checked) + svg {
    stroke: ${themeGet('colors.sys.neutral.border.default')};
  }

  &:disabled + svg {
    stroke: ${themeGet('colors.sys.neutral.text.inactive')};
  }

  &:disabled:checked + svg {
    fill: ${themeGet('colors.sys.neutral.text.inactive')};
    stroke: none;
  }

  &:focus-visible {
    outline: none;
  }

  &:focus-visible + svg {
    border-radius: ${themeGet('radii.small')};
    ${outlineCSS};
  }
`;

const inputStyles: ComponentProps<typeof Input> = {
  type: 'checkbox',
  opacity: 0,
  position: 'absolute',
  role: 'checkbox',
};

const CheckboxIcon = styled(Icon)<CheckboxIconProps>`
  ${fill};
  ${stroke};
  ${display};
  ${minWidth};
  ${minHeight};
  ${backgroundColor};
  margin-right: ${({ direction }) => (direction === Direction.Rtl ? 0 : spaceHelper('sp_8'))};
  transition: fill 0.3s ease-in-out;
`;

const checkboxIconStyles: Partial<ComponentProps<typeof CheckboxIcon>> = {
  display: 'inline-block',
  minWidth: CHECKBOX_SIZE,
  minHeight: CHECKBOX_SIZE,
  backgroundColor: 'transparent',
  stroke: 'sys.neutral.icon.default',
  fill: 'sys.neutral.background.default',
};

const LabelText = styled(BodyRegularBR2)<LabelTextProps>`
  ${flex};
  ${textAlign};

  a {
    ${props => linkStyles({ styled: true, textDecoration: 'underline', ...props })};
  }
`;

const labelTextStyles: LabelTextProps = {
  textAlign: 'left',
  flex: '1 1 0%',
};

const ImageWrapper = styled(ImageOption)<Pick<CheckboxProps, 'direction'>>`
  ${space};
  margin-right: ${({ direction }) => (direction === Direction.Rtl ? spaceHelper('sp_12') : 0)};
`;

// eslint-disable-next-line complexity
const Checkbox = ({
  label = null,
  ariaLabel,
  ariaLabelledBy,
  checked = false,
  disabled,
  value,
  name,
  onChange = () => null,
  className,
  direction = Direction.Ltr,
  imageUrl,
  imageAlt,
  children,
  iconSize = CHECKBOX_SIZE,
  eventName,
  trackingParams,
  hasError,
  imageSize,
  fillImage = false,
  tabIndex,
  onClick,
  onKeyDown = () => null,
}: CheckboxProps) => {
  const id = useId();
  const checkboxId = `checkbox-${id}`;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(event);

    if (eventName) {
      const { checked: latestChecked } = event.target;
      dispatchTrackingEventGA4({
        event: eventName,
        element: 'checkbox',
        interaction: latestChecked
          ? CheckboxAnalyticsValue.Select
          : CheckboxAnalyticsValue.Deselect,
        ...(typeof label === 'string' && { checkbox_text: label }),
        ...trackingParams,
      });
    }
  };

  return (
    <Label
      {...labelStyles}
      data-testid="checkbox-label"
      className={className}
      htmlFor={checkboxId}
      direction={direction}
      hasError={hasError}
      onClick={e => e.stopPropagation()}
    >
      <Input
        {...inputStyles}
        type="checkbox"
        id={checkboxId}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        checked={checked}
        data-testid="checkbox"
        name={name}
        value={value}
        disabled={disabled}
        {...(tabIndex ? { tabIndex } : {})}
        {...(ariaLabelledBy ? { 'aria-labelledby': ariaLabelledBy } : {})}
      />
      <CheckboxIcon
        {...checkboxIconStyles}
        name={getCheckboxIconName({ checked })}
        direction={direction}
        minWidth={iconSize}
        minHeight={iconSize}
        size={iconSize}
      />
      {label ? (
        <LabelText
          {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
          {...labelTextStyles}
          {...(onClick ? { onClick } : {})}
          data-testid="checkbox-label-text"
        >
          {label}
        </LabelText>
      ) : (
        <VisuallyHidden>{ariaLabel || 'Checkbox'}</VisuallyHidden>
      )}
      {imageUrl && (
        <ImageWrapper
          {...(imageSize ? { size: imageSize } : {})}
          url={imageUrl}
          alt={imageAlt}
          disabled={disabled}
          direction={direction}
          noGaps={true}
          fillImage={fillImage}
        />
      )}
      {children}
    </Label>
  );
};

export default Checkbox;
